import { isLink } from './graph-helper';

/**
 * Builds a haystack string for full-text search across multiple node fields.
 */
const buildHaystack = (fields) =>
  fields.filter(Boolean).join(' | ').toLowerCase();

/**
 * Extracts searchable nodes from the current chart items and suggested nodes.
 * Filters out links and maps nodes to a searchable format.
 * 
 * @param {Object} chartItems - The items currently in the chart (nodes and links).
 * @param {Object} allNodes - The complete dictionary of nodes from Redux.
 * @param {Object} [suggestedNodes] - AI-suggested nodes (keyed by id).
 * @returns {Array} Array of searchable node objects { id, label, type, data, isSuggested }.
 */
export const getSearchableNodes = (chartItems, allNodes, suggestedNodes) => {
  if (!chartItems || !allNodes) return [];

  const searchableNodes = [];
  const seenIds = new Set();

  // 1. Existing graph nodes
  Object.keys(chartItems).forEach((key) => {
    const item = chartItems[key];
    if (isLink(item)) return;

    const nodeId = key;
    const nodeData = allNodes[nodeId]?.node?.data;

    if (nodeData) {
      const name = (nodeData.name || nodeData.label || '').trim();
      const type = nodeData.type || nodeData.nodeType;
      const nodeKey = nodeData.nodeKey || nodeId;

      const haystack = buildHaystack([
        name, String(nodeKey), String(nodeId), String(type),
        nodeData.MetroRegion, nodeData.firstname, nodeData.keyname,
        nodeData.maidenname, nodeData.fullname, nodeData.industry,
      ]);

      searchableNodes.push({
        id: nodeId,
        label: name || nodeId,
        type: type,
        haystack,
        data: nodeData,
        isSuggested: false,
        isHidden: false,
      });
      seenIds.add(nodeId);
    }
  });

  // 2. AI-suggested nodes that aren't already in the graph
  if (suggestedNodes) {
    Object.keys(suggestedNodes).forEach((nodeId) => {
      if (seenIds.has(nodeId)) return;

      const sugNode = suggestedNodes[nodeId];
      const data = sugNode.data || {};
      const name = (sugNode.value || data.name || data.label || '').trim();
      const type = data.type || data.nodeType || sugNode.label;

      const haystack = buildHaystack([
        name, String(nodeId), String(type),
        data.MetroRegion, data.firstname, data.keyname,
        data.maidenname, data.fullname, data.industry,
      ]);

      searchableNodes.push({
        id: nodeId,
        label: name || nodeId,
        type: type ? `${type} (Suggested)` : 'Suggested',
        haystack,
        data,
        isSuggested: true,
        isHidden: false,
      });
      seenIds.add(nodeId);
    });
  }

  // 3. Hidden nodes — in allNodes but not visible on the chart (filtered by degree, etc.)
  Object.keys(allNodes).forEach((nodeId) => {
    if (seenIds.has(nodeId)) return;

    const nodeData = allNodes[nodeId]?.node?.data;
    if (!nodeData) return;

    const name = (nodeData.name || nodeData.label || '').trim();
    const type = nodeData.type || nodeData.nodeType;
    const nodeKey = nodeData.nodeKey || nodeId;

    const haystack = buildHaystack([
      name, String(nodeKey), String(nodeId), String(type),
      nodeData.MetroRegion, nodeData.firstname, nodeData.keyname,
      nodeData.maidenname, nodeData.fullname, nodeData.industry,
    ]);

    searchableNodes.push({
      id: nodeId,
      label: name || nodeId,
      type: type ? `${type} (Hidden)` : 'Hidden',
      haystack,
      data: nodeData,
      isSuggested: false,
      isHidden: true,
    });
  });

  return searchableNodes;
};

/**
 * Sorts search results by relevance then alphabetically.
 * Order: prefix > substring; visible > hidden > suggested; alphabetical.
 */
export const sortSearchResults = (results, query) => {
  if (!query) return results;
  const lowerQuery = query.toLowerCase();

  const rank = (node) => {
    if (node.isSuggested) return 2;
    if (node.isHidden) return 1;
    return 0;
  };

  return [...results].sort((a, b) => {
    const aLabel = (a.label || '').toLowerCase();
    const bLabel = (b.label || '').toLowerCase();
    const aPrefix = aLabel.startsWith(lowerQuery);
    const bPrefix = bLabel.startsWith(lowerQuery);

    // Prefix matches first
    if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
    // Visible > hidden > suggested
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    // Alphabetical
    return aLabel.localeCompare(bLabel);
  });
};
